import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Plus, Star, Clock, Users, User, ArrowRight } from 'lucide-react';
import { SingleColumnTemplate } from '../../../../ui/layouts';
import Button from '../../../../ui/canon/Button';
import MyHiringPosts from '../StudentPlatform/pages/MyHiringPosts';
import StudentCandidatesBrowser from './StudentCandidatesBrowser';

const CoachingProgramCard = ({ program }) => {
  const intl = useIntl();
  const coach = program.coachProfile;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow">
      {program.hero_image_url && (
        <div className="h-40 bg-neutral-100 overflow-hidden">
          <img
            src={program.hero_image_url}
            alt={program.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {coach?.avatar_url ? (
            <img
              src={coach.avatar_url}
              alt={coach.name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-succession-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-succession-primary" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-900 truncate">{coach?.name}</p>
            <p className="text-xs text-neutral-500 truncate">{coach?.headline}</p>
            {coach?.org_name && (
              <p className="text-xs text-neutral-400">{coach.org_name}</p>
            )}
          </div>
        </div>

        <h3 className="text-base font-semibold text-neutral-900 mb-2">{program.title}</h3>
        <p className="text-sm text-neutral-600 mb-4 line-clamp-3">{program.summary}</p>

        <div className="flex items-center gap-4 text-xs text-neutral-500 mb-4">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {intl.formatMessage({ id: 'hiring.hub.coaching_card.days' }, { days: program.duration_days })}
          </span>
          <span className="flex items-center gap-1">
            {program.is_group ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
            {program.is_group
              ? intl.formatMessage({ id: 'hiring.hub.coaching_card.group_cohort' })
              : intl.formatMessage({ id: 'hiring.hub.coaching_card.one_on_one' })}
          </span>
          {coach?.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              {coach.rating.toFixed(1)}
            </span>
          )}
        </div>

        {program.benefits && program.benefits.length > 0 && (
          <ul className="space-y-1 mb-4">
            {program.benefits.slice(0, 3).map((benefit, i) => (
              <li key={i} className="text-xs text-neutral-600 flex items-start gap-1.5">
                <span className="text-succession-primary mt-0.5">•</span>
                {benefit}
              </li>
            ))}
          </ul>
        )}

        <a
          href={`/succession/sprint-programs/${program.slug}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-succession-primary hover:text-succession-primary-dark transition-colors"
        >
          {intl.formatMessage({ id: 'hiring.hub.coaching_card.view_program' })}
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const CoachingTab = ({ programs }) => {
  const intl = useIntl();

  if (!programs || programs.length === 0) {
    return (
      <div className="text-center py-16">
        <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.hub.coaching_empty.heading' })}</h3>
        <p className="text-neutral-500 max-w-md mx-auto">
          {intl.formatMessage({ id: 'hiring.hub.coaching_empty.body' })}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-neutral-600 mb-6">
        {intl.formatMessage({ id: 'hiring.hub.coaching_tab.description' })}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {programs.map(program => (
          <CoachingProgramCard key={program.id} program={program} />
        ))}
      </div>
    </div>
  );
};

const RecruitmentHub = ({
  currentUser,
  posts,
  students,
  messagingWithStudentIds,
  onFilterChange,
  filters,
  companyCountry,
  coachingPrograms,
  marketplaceSprintsEnabled,
}) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState('candidates');

  const activePosts = useMemo(() => {
    return (posts || []).filter(p => p.status === 'published');
  }, [posts]);

  const showCoachingTab = marketplaceSprintsEnabled;

  const tabs = useMemo(() => {
    const t = [
      { key: 'posts', label: intl.formatMessage({ id: 'hiring.hub.tab.my_open_roles' }) },
      { key: 'candidates', label: intl.formatMessage({ id: 'hiring.hub.tab.find_candidates' }) },
    ];
    if (showCoachingTab) {
      t.push({ key: 'coaching', label: intl.formatMessage({ id: 'hiring.hub.tab.coaching' }) });
    }
    return t;
  }, [showCoachingTab, intl]);

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'hiring.hub.title' })}
      subtitle={intl.formatMessage({ id: 'hiring.hub.subtitle' })}
      actions={
        <Button as="a" href="/hiring/new" size="lg">
          <Plus className="h-5 w-5" />
          {intl.formatMessage({ id: 'hiring.hub.create_new_post' })}
        </Button>
      }
    >
      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6">
        <div className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 px-1 border-b-2 transition-colors font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-succession-primary text-succession-primary'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'posts' && (
        <MyHiringPosts posts={posts} hideHeader />
      )}
      {activeTab === 'candidates' && (
        <StudentCandidatesBrowser
          students={students}
          activePosts={activePosts}
          messagingWithStudentIds={messagingWithStudentIds}
          currentUser={currentUser}
          onFilterChange={onFilterChange}
          filters={filters}
          companyCountry={companyCountry}
        />
      )}
      {activeTab === 'coaching' && showCoachingTab && (
        <CoachingTab programs={coachingPrograms} />
      )}
    </SingleColumnTemplate>
  );
};

RecruitmentHub.propTypes = {
  currentUser: PropTypes.object,
  posts: PropTypes.array,
  students: PropTypes.array,
  messagingWithStudentIds: PropTypes.instanceOf(Set),
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.object,
  companyCountry: PropTypes.string,
  coachingPrograms: PropTypes.array,
  marketplaceSprintsEnabled: PropTypes.bool,
};

export default RecruitmentHub;
